import React, { useState, useMemo, useEffect, useCallback } from "react";
import axios from "axios";
import { Assets, AssetType } from "../../../../types/asset-type";
import { ListingType } from "../../../../types/userListing";
import { MediaItem } from "../../../../components/listings/listing/types";
import { useSelector } from "react-redux";
import { Label } from "../../Label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../Tabs";
import { ContentReviewStepProps, PlatformGraphics, PlatformGraphicsMap } from "../types";
import { format } from "date-fns";
import { ContentLength } from "../../../campaign/types";
import { Post } from "../../../../types/posts";
import { Media, MediaType } from "../../../../types/media";
import FacebookPost from "../../../post/facebook/FacebookPost";
import InstagramPost from "../../../post/instagram/InstagramPost";
import TwitterPost from "../../../post/twitter/TwitterPost";
import LinkedInPost from "../../../post/linkedin/LinkedInPost";
import PinterestPost from "../../../post/pinterest/PinterestPost";
import GooglePost from "../../../post/google/GooglePost";
import type { RootState } from "../../../../store/store";
import useProfileImage from "../../../../hooks/useProfileImage";
import { Button } from "../../Button";
import { AlertTriangle, ImageIcon } from "lucide-react";
import InlineWarning from "../../InlineWarning";
import SimpleTooltip from "../../SimpleTooltip";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { checkInstagramHashtagViolation } from "../../../../hooks/useInstagramHashtagValidation";
import { PostRelatedMedia } from "../../../post/types";
import { getAuthHeader } from "../../../../utils/authHeader";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import MediaPickerDialog from "../../../post/util/MediaPickerDialog";
import { Post as PostType } from "../../../../types/posts";
import { FaPencil } from "react-icons/fa6";
import EditPostDialog from "../../../post/util/EditPostDialog";
import PostGraphicPickerDialog from "../PostGraphicPickerDialog";
import PostGraphicsEditor from "../PostGraphicsEditor";
import YouTubePost from "../../../post/youtube/YouTubePost";
import DuplicateYouTubeVideoAlert from "../../DuplicateYouTubeVideoAlert";

const ReviewAndScheduleStep = ({
    listingAddress,
    openHouseDate,
    startTime,
    endTime,
    startPeriod,
    endPeriod,
    selectedPlatforms,
    platformContent,
    setPlatformContent,
    platformSchedules,
    setPlatformSchedules,
    selectedMedia,
    availableMedia = { images: [], videos: [], assets: [] },
    setSelectedMedia,
    setShouldSyncParentState,
    shouldSyncParentState,
    platformGraphics,
    setPlatformGraphics,
    selectedListing,
    hideDatetime = false,
    setHasScheduleChangesInStep4,
    youtubeProfileUrl,
    platformProfiles,
    initialMediaType,
    onMediaTypeChange,
    isVideo = false,
    isYouTubeShort,
    fetchMedia,
    hideFooter = false,
    hideEditMedia = false,
    instagramHashtagViolated = false,
}: ContentReviewStepProps) => {
    const isMyBrandListing =
        selectedListing?.listingType?.toLowerCase().replace(/\s/g, "") ===
        "mybrand";
    const instagramContent = platformContent["instagram" as keyof typeof platformContent]?.content ?? "";
    const { errorMessage: hashtagErrorMessage } = checkInstagramHashtagViolation(instagramContent, "instagram");

    const user = useSelector((state: RootState) => state.auth.user);
    const profileImage = useProfileImage(user);
    const isMobile = useIsMobile(900);
    
    const [activeTab, setActiveTab] = useState(
        selectedPlatforms[0]?.id.toLowerCase()
    );
    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState<boolean>(false);
    const [isMediaPickerSaving, setIsMediaPickerSaving] = useState<boolean>(false);
    const [showDuplicateVideoAlert, setShowDuplicateVideoAlert] = useState<boolean>(false);
    const [tempSelectedMedia, setTempSelectedMedia] = useState<string[]>([]);
    const [isPostGraphicPickerOpen, setIsPostGraphicPickerOpen] = useState<boolean>(false);
    const [post, setPost] = useState<PostType | null>(null);

    // The segmented media-type control (MB-1492, extended to listing flows in
    // MB-1496) - video showcase and Promotion keep the tab dialog.
    const useMediaTypeSegments = !isVideo && post?.type !== "Promotion";

    // Derive safe media directly from availableMedia so MediaPickerDialog always
    // reflects the latest state (including newly uploaded assets) in the same render
    // cycle — avoiding the timing race where uploadOccurred fires before assets update.
    const safeAvailableMedia = useMemo((): PostRelatedMedia => ({
        images: (availableMedia.images || []).map(img => ({
            ...img,
            tag: typeof img.tag === 'string' ? img.tag : '',
        })),
        videos: availableMedia.videos || [],
        assets: (availableMedia.assets || [])
            .map(asset => ({ ...asset, assetType: (asset.assetType || 'Image') as AssetType }))
            .filter(asset => !!asset.assetType),
        archived: [],
    }), [availableMedia]);

    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
    const [dialogOpenedForTab, setDialogOpenedForTab] = useState<string | null>(null);
    const [frozenImageText, setFrozenImageText] = useState<string>("");

    // Validate selected video against YouTube before closing edit-media dialog.
    // Only runs when YouTube is a selected platform and a video with a hash is chosen.
    const handleMediaPickerSave = async () => {
        const isYouTubeSelected = selectedPlatforms.some(
            (p) => p.id.toLowerCase() === "youtube"
        );

        if (isYouTubeSelected) {
            const allVideos = [
                ...(safeAvailableMedia.videos ?? []),
                ...(safeAvailableMedia.assets ?? []).filter(
                    (a) => (a as { assetType?: string }).assetType === "Video"
                ),
            ];
            const selectedVideo = allVideos.find((v) =>
                tempSelectedMedia.includes(v.id)
            );
            const videoHash = (selectedVideo as { videoHash?: string })
                ?.videoHash;

            if (videoHash) {
                setIsMediaPickerSaving(true);
                try {
                    const headers = await getAuthHeader();
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;
                    const { data } = await axios.post(
                        `${backendUrl}/post/youtube/check-video-hash`,
                        { videoHashes: [videoHash] },
                        { headers }
                    );
                    if (data?.available === false) {
                        setShowDuplicateVideoAlert(true);
                        return; // keep dialog open, don't commit
                    }
                } catch {
                    // API failure — don't block the user
                } finally {
                    setIsMediaPickerSaving(false);
                }
            }
        }

        setSelectedMedia(tempSelectedMedia);
        setIsMediaPickerOpen(false);
    };
    
    // Helper function to get the scheduled date for a specific platform
    const getScheduledDateForPlatform = (platformKey: string): Date => {
        const schedule = platformSchedules[platformKey];
        if (!schedule) return new Date();

        const scheduledDate = new Date(schedule.date);
        if (isNaN(scheduledDate.getTime())) return new Date();
        let hour = schedule.hour;
        if (schedule.period === 'PM' && hour !== 12) {
            hour += 12;
        } else if (schedule.period === 'AM' && hour === 12) {
            hour = 0;
        }
        scheduledDate.setHours(hour, schedule.minute, 0, 0);
        return scheduledDate;
    };

    
    
    // Get current platform graphics
    const getCurrentPlatformGraphics = (): PlatformGraphics => {
        if (!platformGraphics) {
            return {
                bgColor: { type: 'solid' as const, color: '#ffffff' },
                textColor: '#000000',
                fontSize: 24,
                fontFamily: 'Roboto'
            };
        }
        return platformGraphics[activeTab as keyof PlatformGraphicsMap] || {
            bgColor: { type: 'solid' as const, color: '#ffffff' },
            textColor: '#000000',
            fontSize: 24,
            fontFamily: 'Roboto'
        };
    };

    // Update current platform graphics
    const updatePlatformGraphics = (updates: Partial<PlatformGraphics>) => {
        if (!setPlatformGraphics) return;
        
        setPlatformGraphics(prev => ({
            ...prev,
            [activeTab]: {
                ...prev[activeTab as keyof PlatformGraphicsMap],
                ...updates
            }
        }));
    };

    // Helper functions for handling SetStateAction types
    const handleSetBgColor = (value: React.SetStateAction<{ type: 'solid' | 'gradient'; color?: string; from?: string; to?: string; direction?: string; }>) => {
        const currentGraphics = getCurrentPlatformGraphics();
        const newValue = typeof value === 'function' ? value(currentGraphics.bgColor) : value;
        updatePlatformGraphics({ bgColor: newValue });
    };

    const handleSetTextColor = (value: React.SetStateAction<string>) => {
        const currentGraphics = getCurrentPlatformGraphics();
        const newValue = typeof value === 'function' ? value(currentGraphics.textColor) : value;
        updatePlatformGraphics({ textColor: newValue });
    };

    const handleSetFontSize = (value: React.SetStateAction<number>) => {
        const currentGraphics = getCurrentPlatformGraphics();
        const newValue = typeof value === 'function' ? value(currentGraphics.fontSize) : value;
        updatePlatformGraphics({ fontSize: newValue });
    };

    const handleSetFontFamily = (value: React.SetStateAction<string>) => {
        const currentGraphics = getCurrentPlatformGraphics();
        const newValue = typeof value === 'function' ? value(currentGraphics.fontFamily) : value;
        updatePlatformGraphics({ fontFamily: newValue });
    };
    
     // DO NOT reset shouldSyncParentState to false after editing
     // Once the user manually edits content, keep it true to prevent parent from overwriting
     // The parent will only regenerate content when shouldSyncParentState is false


    // Utility function to map selected media IDs to Media[]
    const getSelectedMedia = (
        selectedIds: string[],
        availableMedia: { images?: MediaItem[]; videos?: MediaItem[]; assets?: MediaItem[] }
    ): Media[] => {
        // Map MediaItem[] to Images[], ListingVideos[], Assets[]
        const images = (availableMedia.images || []).map((img) => ({
            id: img.id,
            url: img.url,
            signedUrl: img.signedUrl,
            thumbnail: img.thumbnail,
            slideshow: undefined,
            name: img.name,
            tag: typeof img.tag === 'string' ? img.tag : '',
            isMls: img.isMls,
            type: 'image' as const,
        }));
        const videos = (availableMedia.videos || []).map((vid) => ({
            id: vid.id,
            url: vid.url,
            signedUrl: vid.signedUrl,
            name: vid.name,
            isMls: vid.isMls,
            isProcessing: vid.isProcessing,
            thumbnail: vid.thumbnail,
            type: 'video' as const,
        }));
        const assets = (availableMedia.assets || []).map((asset) => ({
            id: asset.id,
            name: asset.name,
            url: asset.url,
            signedUrl: asset.signedUrl,
            dateCreated: undefined,
            thumbnail: asset.thumbnail,
            slideshow: undefined,
            assetType: (asset.assetType && typeof asset.assetType === 'string' ? asset.assetType : 'Image') as AssetType,
            isProcessing: asset.isProcessing,
            type: (asset.assetType === 'Video' ? 'video' : 'image') as 'video' | 'image',
        }));
        const videoIds = new Set(videos.map((v) => v.id));
        const assetIds = new Set(assets.map((a) => a.id));
        const allMedia = [
            ...images,
            ...videos,
            ...assets,
        ];
        return selectedIds
            .map((id) => {
                const found = allMedia.find((m) => m.id === id);
                if (!found) return undefined;
                let mediaType: MediaType;
                const isVideo = videoIds.has(id);
                const isAsset = assetIds.has(id);
                if (isVideo) {
                    mediaType = MediaType.ListingVideo;
                } else if (isAsset) {
                    const assetType = ('assetType' in found) ? (found as Assets).assetType : undefined;
                    mediaType = assetType === 'Video' ? MediaType.AssetVideo : MediaType.AssetImage;
                } else {
                    mediaType = MediaType.ListingImage;
                }
                return {
                    id: found.id,
                    url: found.url || "",
                    signedUrl: found.signedUrl || found.url || "",
                    type: mediaType,
                    thumbnail: found.thumbnail,
                    isProcessing: 'isProcessing' in found ? found.isProcessing || false : false,
                    dateCreated: new Date(),
                } as Media;
            })
            .filter((m): m is Media => m !== undefined);
    };

    // AI-enhanced flow uses conservative limits that match EditPostDialog (hideFooter=false).
    // Manual flow uses actual platform limits (hideFooter=true).
    const CONTENT_CHAR_LIMITS: Record<string, number> = hideFooter ? {
        facebook: 50000,
        instagram: 2200,
        twitter: 3000,
        linkedin: 3000,
        pinterest: 500,
        google: 1500,
        gmb: 1500,
        youtube: 5000,
    } : {
        facebook: 50000,
        instagram: 1600,
        twitter: 3700,
        linkedin: 2100,
        pinterest: 200,
        google: 1000,
        gmb: 1000,
        youtube: 4700,
    };

    const getPlatformContentOverage = (key: string): number => {
        const content = platformContent[key as keyof typeof platformContent]?.content || "";
        const limit = CONTENT_CHAR_LIMITS[key] ?? 1500;
        return Math.max(0, content.length - limit);
    };

    // Transform selectedMedia IDs to Media[] array for preview
    const previewMedia = useMemo((): Media[] => {
        return getSelectedMedia(selectedMedia, availableMedia || {});
    }, [selectedMedia, availableMedia]);

    const PLATFORM_MEDIA_LIMITS: Record<string, number> = {
        facebook: 10,
        instagram: 10,
        twitter: 4,
        linkedin: 9,
        pinterest: 1,
        google: 1,
        gmb: 1,
    };

    // Create a mock Post object for preview
    const createPreviewPost = useCallback(
  (platformKey: string): Post => {
    const content =
      platformContent[platformKey as keyof typeof platformContent]?.content ?? "";
    const imageText =
      platformContent[platformKey as keyof typeof platformContent]?.imageText ?? "";
    const title =
      platformContent[platformKey as keyof typeof platformContent]?.title ?? "";
    // Hashtags are now part of content directly — passing via youtubeHashtags field is disabled.
    // const youtubeHashtags =
    //   platformKey === "youtube"
    //     ? (platformContent["youtube"]?.hashtags ?? [])
    //     : undefined;
    const youtubeHashtags = undefined;
    const schedule = platformSchedules[platformKey];
    
    const graphics = platformGraphics ? platformGraphics[platformKey as keyof PlatformGraphicsMap] : {
        bgColor: { type: 'solid' as const, color: '#ffffff' },
        textColor: '#000000',
        fontSize: 24,
        fontFamily: 'Roboto'
    };

    // Check if this is a lead generation post (no media, use background colors)
    const isLeadGeneration = selectedListing?.listingType?.toLowerCase() === "leadgeneration";
    
    return {
      id: "preview",
      campaignId: "",
      scheduleId: "",
      content,
      title,
      imageText: isLeadGeneration ? imageText : undefined,
      media: isLeadGeneration ? [] : previewMedia.slice(0, PLATFORM_MEDIA_LIMITS[platformKey] ?? 10),
      footer: hideFooter ? "" : (schedule?.footer || ""),
      includeFooter: hideFooter ? false : (schedule?.includeFooter || false),
      footerType: hideFooter ? (platformKey === "google" ? "in-post" : "comment") : (schedule?.footerType || (platformKey === "google" ? "in-post" : "comment")),
      platform: platformKey,
      type: "preview",
      status: "draft",
      scheduledFor: getScheduledDateForPlatform(platformKey).toISOString(),
      color: "",
      contentType: "",
      address: listingAddress,
      contentLength: schedule?.contentLength || "Medium",
      displayName: user
        ? `${user.nameFirst || ""} ${user.nameLast || ""}`.trim()
        : "Your Name",
      username: user
        ? `${user.nameFirst || ""}${user.nameLast || ""}`
            .toLowerCase()
            .replace(/\s+/g, "")
        : "yourusername",
      userImage: platformProfiles?.[platformKey] || profileImage || user?.profileImage || user?.photoURL || "",
      background: isLeadGeneration ? (graphics.bgColor || { type: 'solid', color: '#ffffff' }) : undefined,
      textColor: isLeadGeneration ? (graphics.textColor || '#000000') : undefined,
      fontSize: isLeadGeneration ? (graphics.fontSize || 24) : undefined,
      fontFamily: isLeadGeneration ? (graphics.fontFamily || 'Roboto') : undefined,
      youtubeHashtags: youtubeHashtags,
      profileUrl: platformKey === "youtube" ? youtubeProfileUrl : undefined,
    };
  },
     // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    platformContent,
    platformSchedules,
    previewMedia,
    platformGraphics,
    listingAddress,
    user,
    profileImage,
    platformProfiles,
    selectedListing?.listingType,
    youtubeProfileUrl,
  ]
);

    const renderPlatformPreview = (platformKey: string) => {
        const previewPost = createPreviewPost(platformKey);
        const charLimit = CONTENT_CHAR_LIMITS[platformKey];

        // Render appropriate platform component based on platform
        switch (platformKey) {
            case "facebook":
                return (
                    <FacebookPost
                        post={previewPost}
                        setLoading={() => {}}
                        hideDatetime={hideDatetime}
                        contentCharLimit={charLimit}
                    />
                );
            case "instagram":
                return (
                    <InstagramPost
                        post={previewPost}
                        setLoading={() => {}}
                        hideDatetime={hideDatetime}
                        contentCharLimit={charLimit}
                    />
                );
            case "twitter":
                return (
                    <TwitterPost
                        post={previewPost}
                        setLoading={() => {}}
                        hideDatetime={hideDatetime}
                        contentCharLimit={charLimit}
                    />
                );
            case "linkedin":
                return (
                    <LinkedInPost
                        post={previewPost}
                        setLoading={() => {}}
                        hideDatetime={hideDatetime}
                        contentCharLimit={charLimit}
                    />
                );
            case "pinterest":
                return (
                    <PinterestPost
                        post={previewPost}
                        setLoading={() => {}}
                        hideDatetime={hideDatetime}
                        contentCharLimit={charLimit}
                    />
                );
            case "google":
                return (
                    <GooglePost
                        post={previewPost}
                        setLoading={() => {}}
                        hideDatetime={hideDatetime}
                        contentCharLimit={charLimit}
                    />
                );
            case "youtube":
                return (
                    <div className="max-w-2xl mx-auto">
                        <YouTubePost
                            post={previewPost}
                            initialIsShort={isYouTubeShort}
                            onShortDetected={(isShort) => {
                                setPlatformSchedules((prev) => ({
                                    ...prev,
                                    youtube: {
                                        ...prev.youtube,
                                        contentLength: isShort ? ContentLength.Short : ContentLength.Medium,
                                    },
                                }));
                            }}
                        />
                    </div>
                );
            default:
                return null;
        }
    };



    const handleEditMedia = () => {
        const isLeadGeneration = selectedListing?.listingType.toLowerCase() === "leadgeneration";
        if (isLeadGeneration) {
            setIsPostGraphicPickerOpen(true);
        } else {
            setTempSelectedMedia([...selectedMedia]);
            setIsMediaPickerOpen(true);
        }
    };


// Handler for PostGraphicsEditor save

const handleSaveEdit = (contentValue: string, titleValue: string, footerValue: string, scheduledFor?: string, hashtagsValue?: string[]) => {

    const newTitle = titleValue.slice(0, 100);
    setIsEditDialogOpen(false);

    setPlatformSchedules((prev) => {
        const updated = { ...prev };
        const key = activeTab as keyof typeof updated;
        const prevSchedule = updated[key] ?? {};

        // If scheduledFor is provided, update date, hour, minute, period
        if (scheduledFor) {
            // Track that schedule changes were made in Step 4
            if (setHasScheduleChangesInStep4) {
                setHasScheduleChangesInStep4(true);
            }
            
            const dateObj = new Date(scheduledFor);
            let hour = dateObj.getHours();
            const minute = dateObj.getMinutes();
            let period: "AM" | "PM" = "AM";
            let displayHour = hour;

            if (hour === 0) {
                displayHour = 12;
                period = "AM";
            } else if (hour < 12) {
                displayHour = hour;
                period = "AM";
            } else if (hour === 12) {
                displayHour = 12;
                period = "PM";
            } else {
                displayHour = hour - 12;
                period = "PM";
            }

            updated[key] = {
                ...prevSchedule,
                date: dateObj,
                hour: displayHour,
                minute,
                period,
                footer: footerValue.slice(0, key === "twitter" ? 280 : 300),
            };
        } else {
            updated[key] = {
                ...prevSchedule,
                footer: footerValue.slice(0, key === "twitter" ? 280 : 300),
            };
        }
        return updated;
    });

    setPlatformContent((prev) => {
        const updated = { ...prev };
        const key = activeTab as keyof typeof updated;
        updated[key] = {
            ...(updated[key] ?? {}),
            content: contentValue,
            title: newTitle ? newTitle : updated[key]?.title,
            // Hashtags now in content — updating hashtags state from onSave is disabled.
            // ...(activeTab === "youtube" && hashtagsValue !== undefined && { hashtags: hashtagsValue }),
        };
        return updated;
    });
};

const handlePostGraphicsEditorSave = (editedImageText: string) => {
  // Save the edited image text to the tab that was active when dialog opened
  const tabToSave = dialogOpenedForTab || activeTab;
  
  setPlatformContent((prev) => {
    const updated = { ...prev };
    const key = tabToSave as keyof typeof updated;
    updated[key] = {
      ...(updated[key] ?? {}),
      imageText: editedImageText,
    };
    return updated;
  });
  
  setIsEditDialogOpen(false);
  setDialogOpenedForTab(null);
  setFrozenImageText("");

//   if (setHasScheduleChangesInStep4) {
//     setHasScheduleChangesInStep4(true);
//   }
  // Graphics state is already managed by the parent component through the handlers
};

// Get current platform image text
const getCurrentImageText = (): string => {
  // When dialog is open, return frozen value
  if (isEditDialogOpen && dialogOpenedForTab) {
    return frozenImageText;
  }
  // Otherwise return current tab's imageText
  const imageText = platformContent[activeTab as keyof typeof platformContent]?.imageText ?? "";
  return imageText;
};

console.log("platformSchedules", platformSchedules);


// Update current platform image text
const handleSetImageText = (value: React.SetStateAction<string>) => {
  const tabToUse = isEditDialogOpen && dialogOpenedForTab ? dialogOpenedForTab : activeTab;
  const currentImageText = platformContent[tabToUse as keyof typeof platformContent]?.imageText || 
                          platformContent[tabToUse as keyof typeof platformContent]?.content || '';
  const newValue = typeof value === 'function' ? value(currentImageText) : value;
  
  setPlatformContent((prev) => {
    const updated = { ...prev };
    const key = tabToUse as keyof typeof updated;
    updated[key] = {
      ...(updated[key] ?? {}),
      imageText: newValue,
    };
    return updated;
  });
};

    // const isMobile = useIsMobile(900);

    const handleOpenEditDialog = () => {
      // Set the post for the current active tab/platform before opening the dialog
      setShouldSyncParentState?.(true);
      
      // Track which tab the dialog was opened for and freeze the content and imageText
      setDialogOpenedForTab(activeTab);
      const currentContent = platformContent[activeTab as keyof typeof platformContent]?.content || "";
      const currentImageText = platformContent[activeTab as keyof typeof platformContent]?.imageText || currentContent;
      setFrozenImageText(currentImageText);
      
      // Ensure post is created for the current platform with platform-specific data
      const currentPost = createPreviewPost(activeTab);
      setPost(currentPost);
      
      setIsEditDialogOpen(true);      
    } 

    useEffect(() => {
     // Only update post when tab changes AND dialog is closed
     // For Lead Generation posts, preserve existing content when switching tabs
     if (!isEditDialogOpen) {
       const isLeadGeneration = selectedListing?.listingType?.toLowerCase() === "leadgeneration";
       
       if (isLeadGeneration) {
         // For Lead Generation: only update platform-specific properties, preserve content and imageText
         setPost(prev => {
           if (!prev) return createPreviewPost(activeTab);
           
           const newGraphics = platformGraphics?.[activeTab as keyof PlatformGraphicsMap] || {
             bgColor: { type: 'solid' as const, color: '#ffffff' },
             textColor: '#000000',
             fontSize: 24,
             fontFamily: 'Roboto'
           };
           
           const tabImageText = platformContent[activeTab as keyof typeof platformContent]?.imageText;
           const tabContent = platformContent[activeTab as keyof typeof platformContent]?.content;
           
           return {
             ...prev,
             platform: activeTab,
             scheduledFor: getScheduledDateForPlatform(activeTab).toISOString(),
             background: newGraphics.bgColor,
             textColor: newGraphics.textColor,
             fontSize: newGraphics.fontSize,
             fontFamily: newGraphics.fontFamily,
             content: tabContent || prev.content,
             imageText: tabImageText || tabContent,
           };
         });
       } else {
         // For non-Lead Generation: recreate post normally
         setPost(createPreviewPost(activeTab));
       }
     }
     // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, isEditDialogOpen])

    return (
        <div className="relative space-y-6">
            <div>
                <Label className="text-primary font-semibold">
                    Review Your Post Content
                </Label>
                <div className="mt-4 space-y-6">
                    {openHouseDate && startTime && endTime && (
                        <div className="p-4 border rounded-md">
                            <h3 className="font-semibold mb-2 text-primary">
                                Open House Details
                            </h3>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                    <span className="text-muted-foreground">
                                        Address:
                                    </span>{" "}
                                    {listingAddress}
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Date:
                                    </span>{" "}
                                    {format(openHouseDate, "PPP")}
                                </div>
                                <div>
                                    <span className="text-muted-foreground">
                                        Time:
                                    </span>{" "}
                                    {startTime.hour}:
                                    {startTime.minute === 0
                                        ? "00"
                                        : startTime.minute}{" "}
                                    {startPeriod} - {endTime.hour}:
                                    {endTime.minute === 0
                                        ? "00"
                                        : endTime.minute}{" "}
                                    {endPeriod}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content Preview with Visual Preview */}
                    <div className="space-y-4">
                        <h3 className="font-semibold text-primary">
                            Preview Post Content
                        </h3>
                        {selectedPlatforms && selectedPlatforms.length > 0 && (
                            <>
                                <Tabs
                                    value={activeTab}
                                    onValueChange={setActiveTab}
                                    className="mt-2"
                                >
                                    <TabsList className="flex overflow-x-auto space-x-2">
                                        {selectedPlatforms.map((platform) => {
                                            const tabKey = platform.id.toLowerCase();
                                            const overage = getPlatformContentOverage(tabKey);
                                            const isInstagram = tabKey === "instagram";
                                            const isYoutube = tabKey === "youtube";
                                            const showHashtagWarning = isInstagram && instagramHashtagViolated;
                                            const youtubeHashtagCount = isYoutube
                                                ? (platformContent["youtube" as keyof typeof platformContent]?.content || "").split(/\s+/).filter(t => t.startsWith("#") && t.length > 1).length
                                                : 0;
                                            const showYoutubeHashtagWarning = isYoutube && youtubeHashtagCount > 15;
                                            const hasWarning = showHashtagWarning || showYoutubeHashtagWarning || overage > 0;
                                            const tooltipParts: string[] = [];
                                            if (overage > 0) tooltipParts.push(`Content exceeds ${platform.name} limit by ${overage} characters`);
                                            if (showHashtagWarning) tooltipParts.push("Too many hashtags, maximum 5 allowed");
                                            if (showYoutubeHashtagWarning) tooltipParts.push(`Too many hashtags (${youtubeHashtagCount}/15), maximum 15 allowed`);
                                            const tooltipMessage = tooltipParts.join(" · ");
                                            const label = tabKey === "twitter"
                                                ? "X (formerly Twitter)"
                                                : platform.name;
                                            return (
                                                <SimpleTooltip
                                                    key={platform.id}
                                                    message={tooltipMessage}
                                                    visible={hasWarning}
                                                    showArrow
                                                >
                                                    <TabsTrigger
                                                        value={tabKey}
                                                        className="flex items-center gap-1.5"
                                                    >
                                                        {hasWarning && <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                                                        {label}
                                                    </TabsTrigger>
                                                </SimpleTooltip>
                                            );
                                        })}
                                    </TabsList>

                                    {selectedPlatforms.map((platform) => {
                                        const key = platform.id.toLowerCase();
                                        const tabContent = platformContent[key as keyof typeof platformContent]?.content || "";
                                        const contentLimit = CONTENT_CHAR_LIMITS[key] ?? 1500;
                                        const overage = Math.max(0, tabContent.length - contentLimit);
                                        const isOver = overage > 0;
                                        const isInstagramTab = key === "instagram";
                                        const isYoutubeTab = key === "youtube";
                                        const tabHashtagViolated = isInstagramTab && instagramHashtagViolated;
                                        const youtubeTabHashtagCount = isYoutubeTab
                                            ? tabContent.split(/\s+/).filter(t => t.startsWith("#") && t.length > 1).length
                                            : 0;
                                        const youtubeHashtagViolated = isYoutubeTab && youtubeTabHashtagCount > 15;
                                        const errorParts: string[] = [];
                                        if (tabHashtagViolated) errorParts.push(hashtagErrorMessage);
                                        if (youtubeHashtagViolated) errorParts.push(`YouTube allows a maximum of 15 hashtags in the description. You currently have ${youtubeTabHashtagCount}.`);
                                        if (isOver) errorParts.push(`Content exceeds ${platform.name} limit by ${overage} characters. Edit your post to continue.`);
                                        const errorNote = errorParts.join("\n");
                                        return (
                                          <TabsContent
                                            key={platform.id}
                                            value={key}
                                          >
                                            {/* Visual Preview */}
                                            <div className="mb-4 border rounded-md p-4 bg-muted/30">
                                              <div className="flex justify-center">
                                                <div
                                                  style={{
                                                    wordBreak: "break-word",
                                                    overflowWrap: "anywhere",
                                                    width: "100%",
                                                    overflowX: "hidden",
                                                  }}
                                                >
                                                  {renderPlatformPreview(key)}
                                                </div>
                                              </div>
                                            </div>

                                            {/* Error note: over-limit and/or hashtag violation */}
                                            {errorNote && (
                                              <InlineWarning message={errorNote} className="mb-4" />
                                            )}

                                            {/* Edit buttons */}
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: "10px",
                                              }}
                                            >
                                              <Button
                                                id="edit-post-btn"
                                                size="sm"
                                                onClick={() =>
                                                  handleOpenEditDialog()
                                                }
                                                className={`flex items-center justify-center ${
                                                  isMobile ? "w-full mb-2" : ""
                                                }`}
                                              >
                                                <FaPencil className="w-4 h-4 mr-2" />
                                                Edit
                                              </Button>
                                              {selectedListing?.listingType.toLowerCase() !==
                                                "leadgeneration" && !hideEditMedia && (
                                                <Button
                                                  id="edit-media-btn"
                                                  size="sm"
                                                  variant="secondary"
                                                  onClick={handleEditMedia}
                                                  className={`flex items-center justify-center ${
                                                    isMobile ? "w-full" : ""
                                                  }`}
                                                >
                                                  <ImageIcon
                                                    className={`w-4 h-4 ${
                                                      isMobile ? "m-0" : "mr-2"
                                                    }`}
                                                  />
                                                  {!isMobile && "Edit Media"}
                                                </Button>
                                              )}
                                            </div>


                                            {/* New MediaPickerDialog — same design as Select Media step */}
                                            <MediaPickerDialog
                                              isOpen={isMediaPickerOpen}
                                              onClose={() => setIsMediaPickerOpen(false)}
                                              onNext={handleMediaPickerSave}
                                              media={safeAvailableMedia}
                                              selectedMediaIds={tempSelectedMedia}
                                              setSelectedMediaIds={setTempSelectedMedia}
                                              isMobile={isMobile}
                                              platform={selectedPlatforms
                                                  .map((p) => p.id.toLowerCase())
                                                  .join(",") || post?.platform || activeTab || ""}
                                              listingId={selectedListing?.id || ""}
                                              listingType={selectedListing?.listingType as ListingType | undefined}
                                              fetchMedia={fetchMedia ?? (() => Promise.resolve())}
                                              uploadsEnabled={false}
                                              isVideo={isVideo}
                                              isNext={false}
                                              initialMediaType={initialMediaType}
                                              mediaTypeSegments={useMediaTypeSegments}
                                              onMediaTypeChange={useMediaTypeSegments ? onMediaTypeChange : undefined}
                                              sharedMediaOnly={
                                                // My Brand always shows only Shared Media (no listing-level video tab)
                                                isMyBrandListing
                                                || (
                                                  initialMediaType !== "video" &&
                                                  !(availableMedia?.videos || []).some((v) => selectedMedia.includes(v.id)) &&
                                                  !selectedPlatforms.some((p) => p.id.toLowerCase() === "youtube") &&
                                                  !selectedListing?.id
                                                )
                                              }
                                              isSaving={isMediaPickerSaving}
                                            />
                                            <DuplicateYouTubeVideoAlert
                                                open={showDuplicateVideoAlert}
                                                onClose={() => setShowDuplicateVideoAlert(false)}
                                            />

                                            {post &&
                                              (selectedListing?.listingType.toLowerCase() ===
                                              "leadgeneration" ? (
                                                <PostGraphicsEditor
                                                  isOpen={isEditDialogOpen}
                                                  onClose={() =>
                                                    setIsEditDialogOpen(false)
                                                  }
                                                  onSave={
                                                    handlePostGraphicsEditorSave
                                                  }
                                                  bgColor={
                                                    getCurrentPlatformGraphics()
                                                      .bgColor
                                                  }
                                                  setBgColor={handleSetBgColor}
                                                  textColor={
                                                    getCurrentPlatformGraphics()
                                                      .textColor
                                                  }
                                                  setTextColor={
                                                    handleSetTextColor
                                                  }
                                                  fontSize={
                                                    getCurrentPlatformGraphics()
                                                      .fontSize
                                                  }
                                                  setFontSize={
                                                    handleSetFontSize
                                                  }
                                                  fontFamily={
                                                    getCurrentPlatformGraphics()
                                                      .fontFamily
                                                  }
                                                  setFontFamily={
                                                    handleSetFontFamily
                                                  }
                                                  imageText={getCurrentImageText()}
                                                  setImageText={
                                                    handleSetImageText
                                                  }
                                                  previewText={getCurrentImageText()}
                                                  scheduledFor={getScheduledDateForPlatform(
                                                    activeTab,
                                                  )}
                                                  setScheduledFor={(value) => {
                                                    const newDate =
                                                      typeof value ===
                                                      "function"
                                                        ? value(
                                                            getScheduledDateForPlatform(
                                                              activeTab,
                                                            ),
                                                          )
                                                        : value;
                                                    const hour =
                                                      newDate.getHours();
                                                    const minute =
                                                      newDate.getMinutes();
                                                    let period: "AM" | "PM" =
                                                      "AM";
                                                    let displayHour = hour;

                                                    if (hour === 0) {
                                                      displayHour = 12;
                                                      period = "AM";
                                                    } else if (hour < 12) {
                                                      displayHour = hour;
                                                      period = "AM";
                                                    } else if (hour === 12) {
                                                      displayHour = 12;
                                                      period = "PM";
                                                    } else {
                                                      displayHour = hour - 12;
                                                      period = "PM";
                                                    }

                                                    setPlatformSchedules(
                                                      (prev) => ({
                                                        ...prev,
                                                        [activeTab]: {
                                                          ...prev[activeTab],
                                                          date: newDate,
                                                          hour: displayHour,
                                                          minute,
                                                          period,
                                                        },
                                                      }),
                                                    );
                                                  }}
                                                />
                                              ) : (
                                                <EditPostDialog
                                                  isOpen={isEditDialogOpen}
                                                  onClose={() =>
                                                    setIsEditDialogOpen(false)
                                                  }
                                                  post={post}
                                                  setIsOpen={
                                                    setIsEditDialogOpen
                                                  }
                                                  shouldSyncParentState={
                                                    shouldSyncParentState
                                                  }
                                                  bgColor={
                                                    getCurrentPlatformGraphics()
                                                      .bgColor
                                                  }
                                                  setBgColor={handleSetBgColor}
                                                  textColor={
                                                    getCurrentPlatformGraphics()
                                                      .textColor
                                                  }
                                                  setTextColor={
                                                    handleSetTextColor
                                                  }
                                                  fontSize={
                                                    getCurrentPlatformGraphics()
                                                      .fontSize
                                                  }
                                                  setFontSize={
                                                    handleSetFontSize
                                                  }
                                                  fontFamily={
                                                    getCurrentPlatformGraphics()
                                                      .fontFamily
                                                  }
                                                  setFontFamily={
                                                    handleSetFontFamily
                                                  }
                                                  hideFooter={hideFooter}
                                                  onSave={(updated) =>
                                                    handleSaveEdit(
                                                      updated.content as string,
                                                      updated.title || "",
                                                      updated.footer || "",
                                                      updated.scheduledFor,
                                                      // Hashtags now in content — youtubeHashtags field disabled.
                                                      // Array.isArray(updated.youtubeHashtags) ? updated.youtubeHashtags : undefined,
                                                      undefined,
                                                    )
                                                  }
                                                />
                                              ))}
                                          </TabsContent>
                                        );
                                    })}
                                </Tabs>
                            </>
                        )}
                    </div>
                </div>
            </div>
            
            {/* PostGraphicPicker Dialog for Lead Generation Posts */}

<PostGraphicPickerDialog
  open={isPostGraphicPickerOpen}
  onClose={() => setIsPostGraphicPickerOpen(false)}
  onSave={() => {/* optional: handle save logic */}}
  isMobile={isMobile}
  generalInstructions={platformContent[activeTab as keyof typeof platformContent]?.content || ""}
  bgColor={getCurrentPlatformGraphics().bgColor}
  textColor={getCurrentPlatformGraphics().textColor}
  fontSize={getCurrentPlatformGraphics().fontSize}
  fontFamily={getCurrentPlatformGraphics().fontFamily}
  setBgColor={handleSetBgColor}
  setTextColor={handleSetTextColor}
  setFontSize={handleSetFontSize}
  setFontFamily={handleSetFontFamily}
/>

            {/* {isPostGraphicPickerOpen && (
                <Dialog open={isPostGraphicPickerOpen} onOpenChange={setIsPostGraphicPickerOpen}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                        <DialogHeader className="flex flex-row items-center justify-between">
                            <DialogTitle>Edit Post Graphics</DialogTitle>
                            <button
                                onClick={() => setIsPostGraphicPickerOpen(false)}
                                className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            >
                                <X className="h-4 w-4" />
                                <span className="sr-only">Close</span>
                            </button>
                        </DialogHeader>
                        <div className="flex-1 overflow-y-auto">
                            <PostGraphicPicker
                                isOpen={isPostGraphicPickerOpen}
                                onClose={() => setIsPostGraphicPickerOpen(false)}
                                isMobile={isMobile}
                                generalInstructions={platformContent[activeTab as keyof typeof platformContent]?.content || ""}
                                bgColor={getCurrentPlatformGraphics().bgColor}
                                textColor={getCurrentPlatformGraphics().textColor}
                                setBgColor={handleSetBgColor}
                                setTextColor={handleSetTextColor}
                            />
                        </div>
                        <div className="flex justify-end pt-4 border-t">
                            <Button
                                onClick={() => {
                                    // State is now managed by parent component
                                    setIsPostGraphicPickerOpen(false);
                                }}
                                className="bg-primary text-primary-foreground hover:bg-primary/90"
                            >
                                Save
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
            )} */}
        </div>
    );
};

export default React.memo(ReviewAndScheduleStep);
